import React, {useCallback, useMemo, useRef, useEffect, useState} from "react";
import {
    Alert,
    Button,
    Card,
    Col,
    DatePicker,
    Divider,
    Form,
    Input,
    InputNumber,
    message,
    Modal,
    Progress,
    Row,
    Select,
    Space,
    Steps,
    Tag,
    Timeline,
    Typography,
    Tooltip,
    Upload,
    Segmented,
    Radio,
    Descriptions
} from "antd";
import {
    ArrowLeftOutlined,
    ArrowRightOutlined,
    BankOutlined,
    CarOutlined,
    CheckCircleOutlined,
    CloudUploadOutlined,
    DollarOutlined,
    FileDoneOutlined,
    FileSearchOutlined,
    FileTextOutlined,
    HomeOutlined,
    SafetyCertificateOutlined,
    SendOutlined,
    ToolOutlined,
    WalletOutlined,
    LineChartOutlined,
    ApartmentOutlined,
    ClockCircleOutlined,
    InfoCircleOutlined,
    TeamOutlined,
    UsergroupAddOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import {useHistory, useParams} from "react-router-dom";
import axiosinstance from "util/Api";
import {CisUI} from "../../../../util/CISUI";
import "./AssetAcquisition.css";

const {Title, Text} = Typography;
const {TextArea} = Input;

/* ============================================================
   Static reference data
   ============================================================ */

const assetTypes = [
    {label: "Building", value: "Church Building", icon: <HomeOutlined/>},
    {label: "Mission House", value: "Mission House", icon: <HomeOutlined/>},
    {label: "Vehicle", value: "Vehicle (Church Van)", icon: <CarOutlined/>},
    {label: "Furniture", value: "Furniture", icon: <BankOutlined/>},
    {label: "Equipment", value: "Equipment", icon: <ToolOutlined/>},
    {label: "Other", value: "Other", icon: <ToolOutlined/>},
];

const priorityLevels = ["Critical", "Urgent", "High", "Medium", "Low"];
const acquisitionMethods = ["Purchase", "Donation", "Transfer", "Lease"];
const assetConditions = ["Excellent", "Good", "Fair", "Ok", "Need Repair/Upgrade"];

const cashFlowMetrics = [
    {name: "adult_membership", label: "Adult Membership", description: "Registered adult members", type: "number", icon: <TeamOutlined/>},
    {name: "tithe_paying_members", label: "Tithe Paying Members", description: "Members actively paying tithe", type: "number", icon: <UsergroupAddOutlined/>},
    {name: "average_monthly_tithes", label: "Average Monthly Tithes", description: "Average tithe received monthly", type: "money", icon: <DollarOutlined/>},
    {name: "average_monthly_remittance", label: "Monthly Remittance", description: "Average monthly remittance", type: "money", icon: <SendOutlined/>},
    {name: "amount_in_coffers_ldf", label: "Amount in Coffers – LDF", description: "Current LDF cash position", type: "money", icon: <BankOutlined/>},
];

const futureCostItems = [
    ["roof_replacement", "Roof Replacement"], ["hvac_systems", "HVAC Systems"],
    ["parking_lots", "Parking Lots"], ["structural_repairs", "Structural Repairs"],
    ["landscaping", "Landscaping"], ["technology_infrastructure", "Technology Infrastructure"],
    ["property_insurance", "Property Insurance"], ["liability_insurance", "Liability Insurance"],
    ["flood_insurance", "Flood Insurance"], ["ministry_coverage", "Ministry Coverage"],
    ["other_cost", "Other Cost"],
];

const incomeQuestions = [
    ["support_ministry_growth", "Will the property support ministry growth?"],
    ["future_membership", "Can it accommodate future membership increases?"],
    ["rental_for_events", "Can portions be rented for events or community programs?"],
    ["reduce_rental_expenses", "Will it reduce current rental expenses?"],
    ["children_ministry", "Will it accommodate the Children Ministry?"],
];

const checklistItems = [
    ["sufficient_funds", "Sufficient funds for down payment or purchase"],
    ["emergency_reserves", "Emergency reserves maintained"],
    ["budget_approval", "Budget approval obtained"],
    ["positive_cash_flow", "Positive cash flow projections"],
    ["loan_affordability", "Loan affordability confirmed"],
    ["property_valuation", "Independent property valuation completed"],
    ["legal_due_diligence", "Legal and due diligence costs budgeted"],
    ["insurance_reviewed", "Insurance costs reviewed"],
    ["future_maintenance", "Future maintenance costs estimated"],
    ["strategic_fit", "Strategic fit confirmed"],
    ["board_approval", "Board or leadership approval secured"],
    ["risk_assessment_completed", "Risk assessment completed"],
];

const riskItems = [
    ["economic_downturns", "Economic downturns"],
    ["membership_fluctuations", "Membership fluctuations"],
    ["giving_pattern_changes", "Changes in giving patterns"],
    ["unexpected_repair_costs", "Unexpected repair costs"],
    ["interest_rate_increases", "Interest rate increases"],
];

const buildingSpecFields = [
    ["location", "Location", "input", true], ["plot_number", "Plot Number", "input", true],
    ["title_deed_number", "Title Deed Number", "input", false], ["land_size", "Land Size", "input", true],
    ["building_size", "Building Size", "input", true], ["current_use", "Current Use", "input", false],
    ["occupancy_capacity", "Occupancy Capacity", "number", true], ["parking_spaces", "Parking Spaces", "number", false],
    ["property_value", "Property Value", "number", true],
];

const vehicleSpecFields = [
    ["manufacturer", "Manufacturer", "input", true], ["brand", "Brand", "input", true],
    ["model_number", "Model Number", "input", true], ["capacity_size", "Capacity / Size", "input", true],
    ["color", "Color", "input", false], ["year_manufactured", "Year Manufactured", "number", true],
    ["asset_value", "Asset Value", "number", true], ["mileage", "Mileage", "number", false],
    ["current_book_value", "Current Book Value", "number", false], ["vin_chassis_number", "VIN / Chassis Number", "input", false],
];

const furnitureSpecFields = [
    ["furniture_type", "Furniture Type", "select", true, ["Chair", "Table", "Desk", "Cabinet", "Bench", "Shelf", "Other"]],
    ["material", "Material", "input", false], ["brand", "Brand", "input", false],
    ["color", "Color", "input", false], ["unit_cost", "Unit Cost", "number", true],
    ["asset_condition", "Condition", "select", true, assetConditions],
];

const equipmentSpecFields = [
    ["equipment_name", "Equipment Name", "input", true], ["brand", "Brand", "input", false],
    ["model", "Model", "input", false], ["serial_number", "Serial Number", "input", false],
    ["warranty_period", "Warranty Period", "input", false], ["asset_value", "Asset Value", "number", true],
    ["asset_condition", "Condition", "select", true, assetConditions],
];

const otherSpecFields = [
    ["asset_name", "Asset Name", "input", true], ["brand", "Brand", "input", false],
    ["model", "Model", "input", false], ["asset_value", "Asset Value", "number", true],
    ["asset_condition", "Condition", "select", true, assetConditions],
];

const STEP_TITLES = ["Request", "Asset Details", "Funding", "Cash Flow", "Risk & Cost", "Documents", "Review"];

const initialFormValues = {
    asset_type: "Church Building",
    priority_level: "Medium",
    quantity: 1,
    require_assistance_from_nation: "No",
};

/* ============================================================
   Helpers
   ============================================================ */

export const formatNumber = (value, userTyping = false, decimals = null) => {
    if (value === undefined || value === null || value === "") return "";
    if (userTyping) return String(value);
    const options = {};
    if (decimals !== null) {
        options.maximumFractionDigits = decimals;
        options.minimumFractionDigits = decimals;
    }
    return Number(value).toLocaleString("en-US", options);
};

export const parseNumber = (value) => {
    if (!value) return "";
    return value.replace(/,/g, "");
};

const selectOptions = (items) => items.map((x) => ({label: x, value: x}));

// Hoisted so these never change identity between renders — some controls
// (Segmented in particular) recompute internal layout when the options
// prop changes reference, so passing a freshly-built array on every
// keystroke is worth avoiding.
const priorityOptions = selectOptions(priorityLevels);
const acquisitionMethodOptions = selectOptions(acquisitionMethods);
const conditionOptions = selectOptions(assetConditions);
const occupancyStatusOptions = selectOptions(["Vacant", "Occupied", "Partially Occupied", "Under Construction", "Under Renovation"]);
const vehicleStatusOptions = selectOptions(["Excellent/New", "Good", "Fair"]);
const fundingSourceOptions = selectOptions(["Church Budget", "Special Offering", "Grant", "Donation"]);
const financingOptionOptions = selectOptions(["Mortgage", "Loan", "Grant", "Non-Financial Support"]);
const nationHelpOptions = selectOptions(["No", "Yes"]);
const paymentModeOptions = selectOptions(["Cash", "Financing", "Installment", "Credit", "Other"]);
const rateTypeOptions = selectOptions(["Fixed", "Variable"]);

const money = (value) =>
    Number(value || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2});

const compactMoney = (value) => {
    const amount = Number(value || 0);
    const abs = Math.abs(amount);
    const format = (divisor, suffix) => {
        const result = amount / divisor;
        const digits = Math.abs(result) >= 100 ? 0 : Math.abs(result) >= 10 ? 1 : 2;
        return `${Number(result.toFixed(digits))}${suffix}`;
    };
    if (abs >= 1_000_000_000) return format(1_000_000_000, "B");
    if (abs >= 1_000_000) return format(1_000_000, "M");
    if (abs >= 1_000) return format(1_000, "K");
    return amount.toLocaleString(undefined, {maximumFractionDigits: 0});
};

const CompactAmount = ({value, prefix = "$"}) => (
    <Tooltip title={`${prefix}${money(value)}`}>
        <span className="aa-compact-amount">{prefix}{compactMoney(value)}</span>
    </Tooltip>
);

const compactValue = (value, type = "number") => {
    const numericValue = Number(value || 0);
    if (type === "money") {
        return new Intl.NumberFormat("en-US", {notation: "compact", maximumFractionDigits: 1}).format(numericValue);
    }
    return numericValue.toLocaleString("en-US");
};

const fullValue = (value, type = "number") => {
    const numericValue = Number(value || 0);
    if (type === "money") {
        return numericValue.toLocaleString("en-US", {minimumFractionDigits: 2, maximumFractionDigits: 2});
    }
    return numericValue.toLocaleString("en-US");
};

/* Which wizard step "owns" a given top-level form field — used to route
   validation errors on submit back to the right stage, and to compute a
   per-step completion status for the ledger. */
const FIELD_TO_STEP = {
    asset_type: 0, request_date: 0, required_by_date: 0, priority_level: 0,
    acquisition_method: 0, quantity: 0, estimated_cost: 0, justification: 0, asset_description: 0,
    asset_specifications: 1,
    financial_readiness: 2, financing_terms: 2,
    cash_flow_analysis: 3,
    future_costs: 4, income_potential: 4, financial_checklist: 4, risk_assessment: 4,
};

const SectionHeader = ({icon, title, desc}) => (
    <div className="aa-section-header-row">
        <div className="aa-icon-box" aria-hidden="true">{icon}</div>
        <div>
            <Title level={4} className="aa-section-title">{title}</Title>
            <Text type="secondary">{desc}</Text>
        </div>
    </div>
);

const MetricCard = ({title, value, icon, status, format = "currency"}) => (
    <Card bordered={false} className={`aa-metric-card ${status || ""}`}>
        <div className="aa-metric-layout">
            <div className="aa-metric-icon" aria-hidden="true">{icon}</div>
            <div className="aa-metric-copy">
                <Text type="secondary">{title}</Text>
                <Title level={4}>{format === "currency" ? <CompactAmount value={value}/> : value}</Title>
            </div>
        </div>
    </Card>
);

const SpecFieldGrid = ({fields, extra}) => (
    <Row gutter={[16, 16]}>
        {fields.map(([name, label, type, required, options]) => (
            <Col xs={24} md={8} key={name}>
                <Form.Item label={label} name={["asset_specifications", name]} rules={required ? [{required: true, message: `${label} is required`}] : []}>
                    {options ? (
                        <Select options={selectOptions(options)}/>
                    ) : type === "number" ? (
                        <InputNumber formatter={(value) => formatNumber(value)} parser={parseNumber} min={0} style={{width: "100%"}}/>
                    ) : (
                        <Input placeholder={label}/>
                    )}
                </Form.Item>
            </Col>
        ))}
        {extra}
    </Row>
);

const YesNoCard = ({label, name, explanationName, explanationWhen}) => (
    <Card size="small">
        <Form.Item label={label} name={name} rules={[{required: true, message: "Please select Yes or No"}]}>
            <Radio.Group>
                <Radio value="Yes">Yes</Radio>
                <Radio value="No">No</Radio>
            </Radio.Group>
        </Form.Item>
        <Form.Item noStyle shouldUpdate={(p, c) => {
            const pv = name.reduce((o, k) => o?.[k], p);
            const cv = name.reduce((o, k) => o?.[k], c);
            return pv !== cv;
        }}>
            {({getFieldValue}) =>
                getFieldValue(name) === explanationWhen ? (
                    <Form.Item label="Explanation" name={explanationName} rules={[{required: true, message: "Explanation is required"}]}>
                        <TextArea rows={3}/>
                    </Form.Item>
                ) : null
            }
        </Form.Item>
    </Card>
);

export default function AssetAcquisitionWorkspace() {
    const [form] = Form.useForm();
    const {id} = useParams();
    const history = useHistory();
    const isEditMode = Boolean(id);

    const [current, setCurrent] = useState(0);
    const [assetType, setAssetType] = useState("Church Building");
    const [nationHelp, setNationHelp] = useState("No");
    const [loading, setLoading] = useState(false);
    const [pageLoading, setPageLoading] = useState(false);
    const [requestId, setRequestId] = useState(id || null);
    const [requestData, setRequestData] = useState(null);
    // Steps the user has successfully passed validation on — drives the
    // ledger's finish/error state and gates forward navigation.
    const [validSteps, setValidSteps] = useState(() => new Set());
    const [maxStepReached, setMaxStepReached] = useState(0);

    const [cashflowdata, setCashflowData] = useState({
        adult_membership: 0,
        tithe_paying_members: 0,
        average_monthly_tithes: 0,
        average_monthly_expense: 0,
        amount_in_coffers_ldf: 0,
        average_monthly_remittance: 0,
        monthly_data: [],
    });

    // Snapshot of the fields that feed the live calculations and the Review
    // step, refreshed on a short debounce. All seven stages stay mounted
    // (see the render below) so a final submit can validate every field
    // regardless of which stage is on screen — which means a naive
    // "watch every field in the whole form" hook would re-render every
    // mounted Segmented/InputNumber/DatePicker on every single keystroke.
    // Snapshotting only on a small set of calculation-relevant fields, with
    // a short debounce, keeps typing smooth and avoids that render storm.
    const [reviewValues, setReviewValues] = useState({asset_type: "Church Building", priority_level: "Medium", quantity: 1});

    const isBuilding = useMemo(() => ["Church Building", "Mission House"].includes(assetType), [assetType]);
    const isVehicle = useMemo(() => String(assetType || "").includes("Vehicle"), [assetType]);
    const isFurniture = assetType === "Furniture";
    const isEquipment = assetType === "Equipment";

    useEffect(() => {
        if (isEditMode) return;
        const loadData = async () => {
            try {
                const res = await axiosinstance.get("cashflowanalysis");
                if (res.data.status === 1) setCashflowData(res.data.data || {});
            } catch (err) {
                console.error("Cash flow load error:", err);
            }
        };
        loadData();
    }, [isEditMode]);

    const reviewTimerRef = useRef(null);
    const liveCalculationFields = useMemo(() => new Set([
        "estimated_cost", "financial_readiness", "cash_flow_analysis",
        "future_costs", "risk_assessment", "financial_checklist", "income_potential",
        "asset_type", "priority_level", "acquisition_method", "justification",
    ]), []);

    const handleValuesChange = useCallback((changedValues, allValues) => {
        const changedField = Object.keys(changedValues || {})[0];
        if (!liveCalculationFields.has(changedField)) return;
        clearTimeout(reviewTimerRef.current);
        reviewTimerRef.current = setTimeout(() => setReviewValues(allValues), 80);
    }, [liveCalculationFields]);

    useEffect(() => () => clearTimeout(reviewTimerRef.current), []);

    /* ---------------- derived, always-live metrics ---------------- */

    const metrics = useMemo(() => {
        const estimatedCost = Number(reviewValues.estimated_cost || 0);

        const pledgedFunds = nationHelp === "Yes"
            ? Number(reviewValues.financial_readiness?.available_funds || 0)
            : Number(reviewValues.financial_readiness?.amount_available || 0);

        const emergencyFund = Number(reviewValues.cash_flow_analysis?.emergency_fund_available || 0);
        const buildingFund = Number(reviewValues.cash_flow_analysis?.building_fund_available || 0);
        // Reserve funds captured on the Cash Flow stage are real money the
        // congregation can draw on, so they now count toward total
        // available funds instead of being collected and never used.
        const availableFunds = pledgedFunds + emergencyFund + buildingFund;

        const monthlyIncome = Number(cashflowdata?.average_monthly_tithes || 0);
        const monthlyExpense = Number(cashflowdata?.average_monthly_expense || 0);
        const anticipatedAmount = Number(reviewValues.financial_readiness?.anticipated_amount || 0);

        const liquidityRatio = monthlyExpense > 0 ? availableFunds / monthlyExpense : 0;
        const debtIncomeRatio = monthlyIncome > 0 ? (anticipatedAmount / (monthlyIncome * 12)) * 100 : 0;
        const operatingReserveMonths = monthlyExpense > 0 ? availableFunds / monthlyExpense : 0;
        const netAssetPosition = availableFunds - anticipatedAmount;

        const monthlyData = cashflowdata?.monthly_data || [];
        let givingTrend = "No Data";
        if (monthlyData.length >= 2) {
            const first = Number(monthlyData[0]?.amount || 0);
            const last = Number(monthlyData[monthlyData.length - 1]?.amount || 0);
            if (last > first * 1.05) givingTrend = "Growing";
            else if (last < first * 0.95) givingTrend = "Declining";
            else givingTrend = "Stable";
        }

        const fundingGap = Math.max(estimatedCost - availableFunds, 0);
        const fundingPercent = estimatedCost > 0 ? Math.min(Math.round((availableFunds / estimatedCost) * 100), 100) : 0;

        const checklist = reviewValues.financial_checklist || {};
        const checklistAnswers = checklistItems.map(([name]) => checklist[name]).filter(Boolean);
        const checklistYes = checklistAnswers.filter((v) => v === "Yes").length;
        const checklistCompletion = Math.round((checklistAnswers.length / checklistItems.length) * 100);
        const checklistHealth = checklistItems.length > 0 ? Math.round((checklistYes / checklistItems.length) * 100) : 0;

        const risk = reviewValues.risk_assessment || {};
        const riskYesCount = riskItems.filter(([name]) => risk[name] === "Yes").length;
        const checklistGapCount = checklistItems.length - checklistYes - (checklistItems.length - checklistAnswers.length);
        // Risk score now reflects both flagged risks *and* unmet checklist
        // items (a "No" there is a real readiness gap), floored at 15 so a
        // heavily exposed request still reads clearly as high-risk.
        const riskScore = Math.max(100 - riskYesCount * 10 - Math.max(checklistGapCount, 0) * 5, 15);

        // Weighted rather than a flat average: funding coverage matters most
        // for a go/no-go decision, checklist and risk inform it.
        const readinessScore = Math.round(fundingPercent * 0.45 + checklistHealth * 0.3 + riskScore * 0.25);

        const approvalLevel =
            estimatedCost >= 100000 ? "National" : estimatedCost >= 50000 ? "Area" : estimatedCost >= 10000 ? "District" : "Local";

        const fundingSource = reviewValues.financial_readiness?.funding_source || "";
        const budgetAllocationStatus = availableFunds >= estimatedCost ? "Available" : "Insufficient";
        const fundingSourceStatus = fundingSource ? "Confirmed" : "Pending";
        const affordabilityStatus = fundingGap <= 0 ? "Affordable" : "Not Affordable";
        const isAffordable = liquidityRatio >= 2 && operatingReserveMonths >= 3;

        const budgetAllocationRemarks = availableFunds >= estimatedCost
            ? "Sufficient budget is available to cover the acquisition."
            : `Additional ${money(fundingGap)} funding is required.`;
        const fundingSourceRemarks = fundingSource
            ? `${fundingSource} has been identified as the primary funding source.`
            : "No funding source has been specified.";
        const affordabilityRemarks = isAffordable
            ? "Financial position indicates that the acquisition is affordable."
            : "Financial capacity should be reviewed before approval.";
        const financeRecommendation = availableFunds >= estimatedCost && isAffordable ? "APPROVED" : "REVIEW REQUIRED";

        return {
            estimatedCost, availableFunds, pledgedFunds, emergencyFund, buildingFund,
            liquidityRatio, debtIncomeRatio, operatingReserveMonths, netAssetPosition, givingTrend,
            fundingGap, fundingPercent, checklistCompletion, checklistHealth, riskScore, readinessScore,
            approvalLevel, fundingSource, budgetAllocationStatus, fundingSourceStatus, affordabilityStatus,
            budgetAllocationRemarks, fundingSourceRemarks, affordabilityRemarks, financeRecommendation,
        };
    }, [reviewValues, nationHelp, cashflowdata]);

    const completion = useMemo(() => Math.round(((current + 1) / STEP_TITLES.length) * 100), [current]);

    /* ---------------- per-step scoped validation ---------------- */

    const specFieldsForType = () => {
        if (isBuilding) return buildingSpecFields;
        if (isVehicle) return vehicleSpecFields;
        if (isFurniture) return furnitureSpecFields;
        if (isEquipment) return equipmentSpecFields;
        return otherSpecFields;
    };

    const stepFieldNames = useCallback((stepIndex) => {
        switch (stepIndex) {
            case 0: {
                const names = ["asset_type", "request_date", "required_by_date", "priority_level", "acquisition_method", "estimated_cost", "justification"];
                if (!isBuilding) names.push("quantity");
                return names;
            }
            case 1: {
                const required = specFieldsForType().filter(([, , , req]) => req).map(([name]) => ["asset_specifications", name]);
                if (isBuilding) required.push(["asset_specifications", "occupancy_status"], ["asset_specifications", "asset_condition"], ["asset_specifications", "roof_condition"]);
                if (isVehicle) required.push(["asset_specifications", "state_of_car"]);
                if (isFurniture || isEquipment || (!isBuilding && !isVehicle)) required.push(["asset_specifications", "asset_condition"]);
                return required;
            }
            case 2: {
                const names = [["financial_readiness", "funding_source"]];
                names.push(nationHelp === "Yes" ? ["financial_readiness", "anticipated_amount"] : ["financial_readiness", "amount_available"]);
                return names;
            }
            case 3:
                return [["cash_flow_analysis", "emergency_fund_available"], ["cash_flow_analysis", "building_fund_available"]];
            case 4: {
                const names = [];
                incomeQuestions.forEach(([name]) => names.push(["income_potential", name]));
                names.push(["income_potential", "classrooms"]);
                checklistItems.forEach(([name]) => names.push(["financial_checklist", name]));
                riskItems.forEach(([name]) => names.push(["risk_assessment", name]));
                return names;
            }
            default:
                return [];
        }
    }, [isBuilding, isVehicle, isFurniture, isEquipment, nationHelp]);

    const goToStep = useCallback((target) => {
        clearTimeout(reviewTimerRef.current);
        setReviewValues(form.getFieldsValue(true));
        setCurrent(target);
        window.scrollTo({top: 0, behavior: "smooth"});
    }, [form]);

    const next = useCallback(async () => {
        try {
            await form.validateFields(stepFieldNames(current));
            setValidSteps((prev) => new Set(prev).add(current));
            setMaxStepReached((prev) => Math.max(prev, current + 1));
            goToStep(Math.min(current + 1, STEP_TITLES.length - 1));
        } catch {
            message.warning("Please complete the required fields on this stage.");
        }
    }, [form, current, stepFieldNames, goToStep]);

    const prev = useCallback(() => goToStep(Math.max(current - 1, 0)), [current, goToStep]);

    const handleLedgerChange = useCallback((target) => {
        if (target <= maxStepReached) {
            goToStep(target);
        } else {
            message.warning("Complete the current stage before jumping ahead.");
        }
    }, [maxStepReached, goToStep]);

    const resetToInitial = useCallback(() => {
        form.resetFields();
        setCurrent(0);
        setAssetType("Church Building");
        setNationHelp("No");
        setRequestId(null);
        setRequestData(null);
        setValidSteps(new Set());
        setMaxStepReached(0);
        setReviewValues({asset_type: "Church Building", priority_level: "Medium", quantity: 1});
    }, [form]);

    const handleAssetTypeChange = useCallback((value) => {
        const quantity = ["Church Building", "Mission House"].includes(value) ? undefined : 1;
        setAssetType(value);
        // Only the dependent fields need setting here — Form.Item already
        // owns and updates "asset_type" itself via its composed onChange,
        // so writing to it a second time here is what previously caused
        // the two controllers to fight and crash with a Maximum update
        // depth exceeded error.
        form.setFieldsValue({asset_specifications: {}, quantity});
        setReviewValues((prev) => ({...prev, asset_type: value, asset_specifications: {}, quantity}));
    }, [form]);

    /* ---------------- Create / Edit / Submit integration ---------------- */

    const parseJson = useCallback((value) => {
        if (!value) return {};
        if (typeof value === "object") return value;
        try { return JSON.parse(value); } catch { return {}; }
    }, []);

    const yesNo = useCallback((value) => {
        if (value === 1 || value === "1" || value === true || String(value).toLowerCase() === "yes") return "Yes";
        if (value === 0 || value === "0" || value === false || String(value).toLowerCase() === "no") return "No";
        return undefined;
    }, []);

    const mapBackendToForm = useCallback((data) => {
        const specifications = parseJson(data.asset_specifications);
        if (specifications.construction_date) specifications.construction_date = dayjs(specifications.construction_date);

        return {
            asset_type: data.asset_type || undefined,
            request_date: data.request_date ? dayjs(data.request_date) : null,
            required_by_date: data.required_by_date ? dayjs(data.required_by_date) : null,
            priority_level: data.priority_level || undefined,
            acquisition_method: data.acquisition_method || undefined,
            quantity: data.quantity !== null && data.quantity !== undefined ? Number(data.quantity) : undefined,
            estimated_cost: data.estimated_cost !== null && data.estimated_cost !== undefined ? Number(data.estimated_cost) : undefined,
            justification: data.justification || "",
            asset_description: data.asset_description || "",
            asset_specifications: specifications,
            financial_readiness: {
                funding_source: data.funding_source || undefined,
                amount_available: data.amount_available !== null ? Number(data.amount_available) : undefined,
                payment_mode: data.payment_mode || undefined,
                anticipated_amount: data.anticipated_amount !== null ? Number(data.anticipated_amount) : undefined,
                available_funds: data.available_funds !== null ? Number(data.available_funds) : undefined,
            },
            financing_terms: {
                interest_rate: data.interest_rate !== null ? Number(data.interest_rate) : undefined,
                loan_term: data.loan_term || undefined,
                monthly_payment: data.monthly_payment !== null ? Number(data.monthly_payment) : undefined,
                rate_type: data.rate_type || undefined,
            },
            cash_flow_analysis: {
                adult_membership: Number(data.adult_membership || 0),
                tithe_paying_members: Number(data.tithe_paying_members || 0),
                average_monthly_tithes: Number(data.average_monthly_tithes || 0),
                average_monthly_remittance: Number(data.average_monthly_remittance || 0),
                amount_in_coffers_ldf: Number(data.amount_in_coffers_ldf || 0),
                emergency_fund_available: Number(data.emergency_fund_available || 0),
                building_fund_available: Number(data.building_fund_available || 0),
            },
            future_costs: parseJson(data.future_costs),
            income_potential: parseJson(data.income_potential),
            financial_checklist: parseJson(data.financial_checklist),
            risk_assessment: parseJson(data.risk_assessment),
            require_assistance_from_nation: yesNo(data.require_assistance_from_nation) || "No",
        };
    }, [parseJson, yesNo]);

    useEffect(() => {

        if (!id) {
            return;
        }

        const loadRequest = async () => {

            setPageLoading(true);

            try {

                const res = await axiosinstance.get(
                    `/assets/assetrequestopen/${id}`
                );

                console.log(
                    "EDIT API RESPONSE:",
                    res.data
                );

                if (res.data.status !== 1) {

                    message.error(
                        res.data.msg ||
                        "Unable to load asset request."
                    );

                    return;
                }

                const data = res.data.data;

                if (!data) {

                    message.error(
                        "Asset request data not found."
                    );

                    return;
                }

                console.log(
                    "EDIT REQUEST DATA:",
                    data
                );

                /* -----------------------------------------
                   Set request state
                   ----------------------------------------- */

                setRequestId(data.id);

                setRequestData(data);

                console.log("EDIT PERMISSION:", {
                    current_level: data.current_level,
                    status: data.status,
                    editable:
                        String(data.current_level || "LOCAL").trim().toUpperCase() === "LOCAL" &&
                        ["DRAFT", "RETURNED"].includes(
                            String(data.status || "DRAFT").trim().toUpperCase()
                        ),
                });

                setAssetType(
                    data.asset_type ||
                    "Church Building"
                );


                /* -----------------------------------------
                   Nation assistance
                   ----------------------------------------- */

                const nationValue =
                    Number(
                        data.require_assistance_from_nation
                    ) === 1
                        ? "Yes"
                        : "No";

                setNationHelp(
                    nationValue
                );


                /* -----------------------------------------
                   JSON helper
                   ----------------------------------------- */

                const jsonObject = (value) => {

                    if (!value) {
                        return {};
                    }

                    if (
                        typeof value === "object"
                    ) {
                        return value;
                    }

                    try {

                        return JSON.parse(
                            value
                        );

                    } catch (e) {

                        console.error(
                            "JSON Parse Error:",
                            value
                        );

                        return {};
                    }
                };


                /* -----------------------------------------
                   Asset Specifications
                   ----------------------------------------- */

                const assetSpecifications =
                    jsonObject(
                        data.asset_specifications
                    );


                if (
                    assetSpecifications
                        .construction_date
                ) {

                    assetSpecifications
                        .construction_date =
                        dayjs(
                            assetSpecifications
                                .construction_date
                        );
                }


                /* -----------------------------------------
                   Build EXACT form structure
                   ----------------------------------------- */

                const formValues = {

                    /* Request */

                    asset_type:
                    data.asset_type,

                    request_date:
                        data.request_date
                            ? dayjs(
                                data.request_date
                            )
                            : null,

                    required_by_date:
                        data.required_by_date
                            ? dayjs(
                                data.required_by_date
                            )
                            : null,

                    priority_level:
                    data.priority_level,

                    acquisition_method:
                    data.acquisition_method,

                    quantity:
                        data.quantity !== null
                            ? Number(
                                data.quantity
                            )
                            : null,

                    estimated_cost:
                        data.estimated_cost !== null
                            ? Number(
                                data.estimated_cost
                            )
                            : null,

                    justification:
                        data.justification || "",

                    asset_description:
                        data.asset_description || "",


                    /* Asset Details */

                    asset_specifications:
                    assetSpecifications,


                    /* Financial Readiness */

                    financial_readiness: {

                        funding_source:
                        data.funding_source,

                        amount_available:
                            data.amount_available !== null
                                ? Number(
                                    data.amount_available
                                )
                                : null,

                        payment_mode:
                        data.payment_mode,

                        anticipated_amount:
                            data.anticipated_amount !== null
                                ? Number(
                                    data.anticipated_amount
                                )
                                : null,

                        available_funds:
                            data.available_funds !== null
                                ? Number(
                                    data.available_funds
                                )
                                : null,
                    },


                    /* Financing */

                    financing_terms: {

                        interest_rate:
                            data.interest_rate !== null
                                ? Number(
                                    data.interest_rate
                                )
                                : null,

                        loan_term:
                        data.loan_term,

                        monthly_payment:
                            data.monthly_payment !== null
                                ? Number(
                                    data.monthly_payment
                                )
                                : null,

                        rate_type:
                        data.rate_type,
                    },


                    /* Cash Flow */

                    cash_flow_analysis: {

                        adult_membership:
                            Number(
                                data.adult_membership || 0
                            ),

                        tithe_paying_members:
                            Number(
                                data.tithe_paying_members || 0
                            ),

                        average_monthly_tithes:
                            Number(
                                data.average_monthly_tithes || 0
                            ),

                        average_monthly_expense:
                            Number(
                                data.average_monthly_expense || 0
                            ),

                        average_monthly_remittance:
                            Number(
                                data.average_monthly_remittance || 0
                            ),

                        amount_in_coffers_ldf:
                            Number(
                                data.amount_in_coffers_ldf || 0
                            ),

                        emergency_fund_available:
                            Number(
                                data.emergency_fund_available || 0
                            ),

                        building_fund_available:
                            Number(
                                data.building_fund_available || 0
                            ),
                    },


                    /* JSON Sections */

                    future_costs:
                        jsonObject(
                            data.future_costs
                        ),

                    income_potential:
                        jsonObject(
                            data.income_potential
                        ),

                    financial_checklist:
                        jsonObject(
                            data.financial_checklist
                        ),

                    risk_assessment:
                        jsonObject(
                            data.risk_assessment
                        ),


                    /* Nation Assistance */

                    require_assistance_from_nation:
                    nationValue,
                };


                /* -----------------------------------------
                   Existing Documents
                   ----------------------------------------- */

                const documents = {

                    LETTER: [],
                    IMAGE: [],
                    OTHER: [],
                };


                (
                    data.documents || []
                ).forEach((doc) => {

                    let type =
                        String(
                            doc.document_type ||
                            "OTHER"
                        ).toUpperCase();


                    if (
                        !documents[type]
                    ) {

                        type = "OTHER";
                    }


                    documents[type].push({

                        uid:
                            `existing-${doc.id}`,

                        name:
                            doc.original_file_name ||
                            doc.file_name,

                        status:
                            "done",

                        url:
                            doc.file_url ||
                            doc.file_path,

                        existing:
                            true,

                        document_id:
                        doc.id,
                    });

                });


                formValues.documents =
                    documents;


                console.log(
                    "SETTING FORM VALUES:",
                    formValues
                );


                /* =========================================
                   IMPORTANT PART
                   ========================================= */

                form.setFieldsValue(
                    formValues
                );


                /*
                 * Keep values for Review page.
                 */

                setReviewValues(
                    formValues
                );


                /*
                 * Make sure dynamic fields use
                 * correct Asset Type.
                 */

                setAssetType(
                    formValues.asset_type
                );


            } catch (error) {

                console.error(
                    "EDIT LOAD ERROR:",
                    error
                );


                message.error(
                    error?.response?.data?.msg ||
                    error?.response?.data?.message ||
                    "Unable to load asset request."
                );


            } finally {

                setPageLoading(false);
            }

        };


        loadRequest();

    }, [
        id,
        form
    ]);

    const canEdit = useMemo(() => {
        // While creating/loading, keep the form interactive.
        if (!requestData) return true;

        // Backend/database values may arrive with different casing or
        // surrounding whitespace. Normalize them before checking workflow.
        const level = String(requestData.current_level || "LOCAL")
            .trim()
            .toUpperCase();

        const status = String(requestData.status || "DRAFT")
            .trim()
            .toUpperCase();

        // Local can edit a new draft and a request returned for correction.
        return level === "LOCAL" && ["DRAFT", "RETURNED"].includes(status);
    }, [requestData]);

    const preparePayload = useCallback((data) => {
        const incomeAnswers = data.income_potential || {};
        const incomePotentialEnabled = Object.entries(incomeAnswers)
            .some(([key, val]) => !key.includes("_explanation") && key !== "classrooms" && val === "Yes");

        const cashFlow = isEditMode
            ? {...(data.cash_flow_analysis || {})}
            : {
                ...(data.cash_flow_analysis || {}),
                adult_membership: Number(cashflowdata?.adult_membership || 0),
                tithe_paying_members: Number(cashflowdata?.tithe_paying_members || 0),
                average_monthly_tithes: Number(cashflowdata?.average_monthly_tithes || 0),
                average_monthly_remittance: Number(cashflowdata?.average_monthly_remittance || 0),
                amount_in_coffers_ldf: Number(cashflowdata?.amount_in_coffers_ldf || 0),
                emergency_fund_available: Number(cashflowdata?.emergency_fund_available || 0),
                building_fund_available: Number(cashflowdata?.building_fund_available || 0),
            };

        return {
            asset_type: data.asset_type || null,
            request_date: data.request_date ? dayjs(data.request_date).format("YYYY-MM-DD") : null,
            required_by_date: data.required_by_date ? dayjs(data.required_by_date).format("YYYY-MM-DD") : null,
            priority_level: data.priority_level || null,
            acquisition_method: data.acquisition_method || null,
            quantity: data.quantity ?? null,
            estimated_cost: data.estimated_cost ?? null,
            justification: data.justification || null,
            asset_description: data.asset_description || null,
            asset_specifications: {
                ...(data.asset_specifications || {}),
                construction_date: data.asset_specifications?.construction_date
                    ? dayjs(data.asset_specifications.construction_date).format("YYYY-MM-DD") : null,
            },
            financial_readiness: data.financial_readiness || {},
            financing_terms: data.financing_terms || {},
            cash_flow_analysis: cashFlow,
            future_costs: data.future_costs || {},
            income_potential: data.income_potential || {},
            financial_checklist: data.financial_checklist || {},
            risk_assessment: data.risk_assessment || {},
            require_assistance_from_nation: data.require_assistance_from_nation === "Yes",
            income_potential_enabled: incomePotentialEnabled,
        };
    }, [cashflowdata, isEditMode]);

    const handleBackendError = useCallback((error) => {
        console.error("Asset request error:", error);
        const response = error?.response?.data;
        const errors = response?.errors;
        if (errors) {
            const fields = Object.entries(errors)
                .filter(([name]) => name !== "required_fields")
                .map(([name, values]) => ({name: name.split("."), errors: Array.isArray(values) ? values : [values]}));
            if (fields.length) {
                form.setFields(fields);
                const target = FIELD_TO_STEP[fields[0]?.name?.[0]] ?? 0;
                setMaxStepReached((prevMax) => Math.max(prevMax, target));
                goToStep(target);
            }
            if (errors.required_fields) {
                message.warning(`Missing: ${errors.required_fields.join(", ")}`);
                return;
            }
            message.warning("Please check the highlighted fields.");
            return;
        }
        message.error(response?.msg || response?.message || error?.message || "Something went wrong.");
    }, [form, goToStep]);

    const saveRequest = useCallback(async (values) => {
        const payload = preparePayload(values);
        const res = requestId
            ? await axiosinstance.post(`/assets/assetrequestedit/${requestId}`, payload)
            : await axiosinstance.post("/assets/newassetrequest", payload);
        if (res.data.status !== 1) {
            const error = new Error(res.data.msg || "Unable to save asset request.");
            error.response = {data: res.data};
            throw error;
        }
        const savedId = requestId || res.data.id || res.data.data?.id;
        if (!savedId) throw new Error("Asset request ID was not returned by backend.");
        setRequestId(savedId);
        return savedId;
    }, [requestId, preparePayload]);

    const uploadDocuments = useCallback(async (assetRequestId, documents = {}) => {
        for (const [documentType, fileList] of Object.entries(documents || {})) {
            if (!Array.isArray(fileList)) continue;
            for (const item of fileList) {
                if (item?.existing || item?.uploaded) continue;
                const file = item?.originFileObj || item;
                if (!(file instanceof File)) continue;
                const formData = new FormData();
                formData.append("document_type", documentType);
                formData.append("document_title", file.name);
                formData.append("file", file);
                const res = await axiosinstance.post(`/assets/assetrequestdocument/${assetRequestId}`, formData, {
                    headers: {"Content-Type": "multipart/form-data"},
                });
                if (res.data.status !== 1) throw new Error(res.data.msg || `Unable to upload ${file.name}`);
                item.uploaded = true;
                item.existing = true;
                item.document_id = res.data.id;
                item.status = "done";
            }
        }
    }, []);

    const removeDocument = useCallback(async (file) => {
        if (!file?.existing || !file?.document_id) return true;
        try {
            const res = await axiosinstance.delete(`/assets/assetrequestdocumentdelete/${file.document_id}`);
            if (res.data.status === 1) {
                message.success("Document removed.");
                return true;
            }
            message.error(res.data.msg || "Unable to remove document.");
            return false;
        } catch (error) {
            message.error(error?.response?.data?.msg || "Unable to remove document.");
            return false;
        }
    }, []);

    const saveDraft = useCallback(async () => {
        if (!canEdit) return message.warning("This request is no longer editable.");
        try {
            await form.validateFields(["asset_type", "request_date"]);
            setLoading(true);
            const values = form.getFieldsValue(true);
            const wasExisting = Boolean(requestId);
            const savedId = await saveRequest(values);
            await uploadDocuments(savedId, values.documents);
            message.success(wasExisting ? "Asset request updated successfully." : "Draft saved successfully.");
            if (!wasExisting) {
                history.replace(`/assets/editassetrequest/${savedId}`);
            }
        } catch (error) {
            if (error?.errorFields) {
                const field = error.errorFields[0]?.name?.[0];
                goToStep(FIELD_TO_STEP[field] ?? 0);
                message.warning("Please complete the required request information.");
                return;
            }
            handleBackendError(error);
        } finally {
            setLoading(false);
        }
    }, [canEdit, form, requestId, saveRequest, uploadDocuments, history, goToStep, handleBackendError]);

    const onFinish = useCallback(async (values) => {
        if (!canEdit) return message.warning("This request is no longer editable.");
        try {
            setLoading(true);
            const savedId = await saveRequest(values);
            await uploadDocuments(savedId, values.documents);
            const returned = requestData?.status === "RETURNED";
            const endpoint = returned
                ? `/assets/assetrequestresubmit/${savedId}`
                : `/assets/assetrequestsubmit/${savedId}`;
            const res = await axiosinstance.post(endpoint, {
                comments: returned
                    ? "Asset acquisition request updated and resubmitted for District assessment."
                    : "Asset acquisition request submitted for District assessment.",
            });
            if (res.data.status !== 1) {
                message.warning(res.data.msg || "Request saved but workflow submission failed.");
                return;
            }
            message.success(returned ? "Asset request resubmitted successfully." : "Asset request submitted successfully.");
            history.push(
                "/assets/assetrequest"
            );
        } catch (error) {
            handleBackendError(error);
        } finally {
            setLoading(false);
        }
    }, [canEdit, saveRequest, uploadDocuments, requestData, history, handleBackendError]);

    const onFinishFailed = useCallback(({errorFields}) => {
        if (!errorFields?.length) return;
        const firstField = errorFields[0]?.name?.[0];
        const targetStep = FIELD_TO_STEP[firstField] ?? 0;
        setMaxStepReached((prevMax) => Math.max(prevMax, targetStep));
        goToStep(targetStep);
        message.warning(`Please complete required fields in "${STEP_TITLES[targetStep]}".`);
    }, [goToStep]);

    const submitRequest = useCallback(() => {
        if (!canEdit) return;
        const run = () => form.submit();
        if (metrics.financeRecommendation === "REVIEW REQUIRED") {
            Modal.confirm({
                title: "Finance review required",
                content: `Based on current funding and risk figures, this request is flagged "REVIEW REQUIRED". You can still submit it for District assessment.`,
                okText: requestData?.status === "RETURNED" ? "Resubmit" : "Submit for review",
                cancelText: "Go back and adjust",
                onOk: run,
            });
            return;
        }
        run();
    }, [canEdit, form, metrics.financeRecommendation, requestData]);

    const stepItems = useMemo(() => STEP_TITLES.map((title, i) => {
        const icons = [<FileTextOutlined key="a"/>, isVehicle ? <CarOutlined key="b"/> : isBuilding ? <HomeOutlined key="b"/> : <ToolOutlined key="b"/>,
            <WalletOutlined key="c"/>, <DollarOutlined key="d"/>, <SafetyCertificateOutlined key="e"/>, <CloudUploadOutlined key="f"/>, <FileSearchOutlined key="g"/>];
        let status = "wait";
        if (i === current) status = "process";
        else if (i < current) status = validSteps.has(i) ? "finish" : "error";
        return {title, icon: icons[i], status};
    }), [current, validSteps, isVehicle, isBuilding]);

    /* ---------------- shared bits ---------------- */

    /* ================= Step 0 — Request ================= */

    const BasicRequest = () => (
        <>
            <SectionHeader icon={<FileTextOutlined/>} title="Request Information"
                           desc="Start with asset category, priority, acquisition method, cost, and justification."/>

            <div className="aa-section">
                <Form.Item label="Asset Type" name="asset_type" required>
                    <Segmented block options={assetTypes} onChange={handleAssetTypeChange}/>
                </Form.Item>

                <Divider/>

                <Row gutter={[16, 16]}>
                    <Col xs={24} md={8}>
                        <Form.Item label="Request Date" name="request_date" rules={[{required: true, message: "Request date is required"}]}>
                            <DatePicker format={CisUI().dateFormat} style={{width: "100%"}}/>
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={8}>
                        <Form.Item
                            label="Required By Date"
                            name="required_by_date"
                            rules={[
                                {required: true, message: "Required by date is required"},
                                {
                                    validator(_, value) {
                                        if (!value || !value.isBefore(dayjs(), "day")) return Promise.resolve();
                                        return Promise.reject(new Error("Date cannot be backdated"));
                                    },
                                },
                            ]}
                        >
                            <DatePicker format={CisUI().dateFormat} style={{width: "100%"}}/>
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={12}>
                        <Form.Item label="Priority Level" name="priority_level" rules={[{required: true, message: "Priority is required"}]}>
                            <Segmented options={priorityOptions} block/>
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={12}>
                        <Form.Item label="Acquisition Method" name="acquisition_method" rules={[{required: true, message: "Acquisition method is required"}]}>
                            <Segmented options={acquisitionMethodOptions} block/>
                        </Form.Item>
                    </Col>

                    {!isBuilding && (
                        <Col xs={24} md={8}>
                            <Form.Item label="Quantity" name="quantity" rules={[{required: true, message: "Quantity is required"}]}>
                                <InputNumber formatter={(value) => formatNumber(value)} parser={parseNumber} min={1} style={{width: "100%"}}/>
                            </Form.Item>
                        </Col>
                    )}

                    <Col xs={24} md={8}>
                        <Form.Item label="Estimated Cost" name="estimated_cost" rules={[{required: true, message: "Estimated cost is required"}]}>
                            <InputNumber formatter={(value) => formatNumber(value)} parser={parseNumber} min={0} style={{width: "100%"}}/>
                        </Form.Item>
                    </Col>

                    <Col xs={24}>
                        <Form.Item label="Justification / Purpose" name="justification" rules={[{required: true, message: "Justification is required"}]}>
                            <TextArea rows={4} placeholder="Explain why this acquisition is needed"/>
                        </Form.Item>
                    </Col>

                    <Col xs={24}>
                        <Form.Item label="Asset Description/Note" name="asset_description">
                            <TextArea rows={4} placeholder="Describe the asset clearly"/>
                        </Form.Item>
                    </Col>
                </Row>
            </div>
        </>
    );

    /* ================= Step 1 — Asset details ================= */

    const AssetDetails = () => {
        if (isBuilding) {
            return (
                <Card className="aa-section" title="Building / Property Specification">
                    <SpecFieldGrid fields={buildingSpecFields} extra={<>
                        <Col xs={24} md={8}>
                            <Form.Item label="Construction Date" name={["asset_specifications", "construction_date"]}>
                                <DatePicker style={{width: "100%"}}/>
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item label="Occupancy Status" name={["asset_specifications", "occupancy_status"]} rules={[{required: true, message: "Occupancy status is required"}]}>
                                <Select options={occupancyStatusOptions}/>
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item label="Asset Condition" name={["asset_specifications", "asset_condition"]} rules={[{required: true, message: "Asset condition is required"}]}>
                                <Select options={conditionOptions}/>
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item label="Roof Condition" name={["asset_specifications", "roof_condition"]} rules={[{required: true, message: "Roof condition is required"}]}>
                                <Select options={conditionOptions}/>
                            </Form.Item>
                        </Col>
                    </>}/>
                </Card>
            );
        }

        if (isVehicle) {
            return (
                <Card className="aa-section" title="Vehicle Specification">
                    <SpecFieldGrid fields={vehicleSpecFields} extra={
                                                                   <Col xs={24} md={8}>
                                                                      <Form.Item label="Vehicle Status" name={["asset_specifications", "state_of_car"]} rules={[{required: true, message: "Vehicle Status is required"}]}>
                                                                          <Select options={vehicleStatusOptions}/>
                                                                      </Form.Item>
                                                                  </Col>
                                                              }/>
                </Card>
            );
        }

        const title = isFurniture ? "Furniture Specification" : isEquipment ? "Equipment Specification" : "Other Asset Specification";
        const fields = isFurniture ? furnitureSpecFields : isEquipment ? equipmentSpecFields : otherSpecFields;
        return <Card className="aa-section" title={title}><SpecFieldGrid fields={fields}/></Card>;
    };

    const AssetDetailsWrapper = () => (
        <>
            <SectionHeader
                icon={isVehicle ? <CarOutlined/> : isBuilding ? <HomeOutlined/> : <ToolOutlined/>}
                title="Asset Specifications"
                desc="Complete dynamic details based on the selected asset type."
            />
            {AssetDetails()}
        </>
    );

    /* ================= Step 2 — Funding ================= */

    const Funding = () => (
        <>
            <SectionHeader icon={<WalletOutlined/>} title="Funding & Financing" desc="Track funding readiness, available money, gap, and financing terms."/>

            <div className="aa-section">
                <Row gutter={[18, 18]}>
                    <Col xs={24} lg={8}>
                        <Card bordered={false} className="aa-funding-card">
                            <Progress type="dashboard" percent={metrics.fundingPercent}/>
                            <Title level={5}>Funding Readiness</Title>
                            <Text type="secondary">Available funds compared with estimated asset cost.</Text>
                        </Card>
                    </Col>

                    <Col xs={24} lg={16}>
                        <Row gutter={[16, 16]}>
                            <Col xs={24} md={8}><MetricCard title="Estimated Cost ($)" value={metrics.estimatedCost} icon={<DollarOutlined/>}/></Col>
                            <Col xs={24} md={8}><MetricCard title="Available Funds" value={metrics.availableFunds} icon={<WalletOutlined/>} status="success"/></Col>
                            <Col xs={24} md={8}><MetricCard title="Funding Gap" value={metrics.fundingGap} icon={<SafetyCertificateOutlined/>} status={metrics.fundingGap > 0 ? "danger" : "success"}/></Col>
                        </Row>
                        {metrics.fundingGap > 0 && (
                            <Alert
                                style={{marginTop: 14}}
                                type="warning"
                                showIcon
                                message={`This request still needs ${money(metrics.fundingGap)} beyond funds currently available (including reserves logged in Cash Flow).`}
                            />
                        )}
                    </Col>
                </Row>

                <Divider/>

                <Row gutter={[16, 16]}>
                    <Col xs={24} md={8}>
                        <Form.Item label="Funding Source" name={["financial_readiness", "funding_source"]} rules={[{required: true, message: "Funding source is required"}]}>
                            <Select options={fundingSourceOptions}/>
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={8}>
                        <Form.Item label="Financing Options">
                            <Select options={financingOptionOptions}/>
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={8}>
                        <Form.Item label="Require Assistance from Nation?" name="require_assistance_from_nation">
                            <Select options={nationHelpOptions} onChange={setNationHelp}/>
                        </Form.Item>
                    </Col>

                    {nationHelp === "Yes" ? (
                        <>
                            <Col xs={24} md={8}>
                                <Form.Item label="Anticipated Amount" name={["financial_readiness", "anticipated_amount"]} rules={[{required: true, message: "Anticipated amount is required"}]}>
                                    <InputNumber formatter={(value) => formatNumber(value)} parser={parseNumber} min={0} style={{width: "100%"}}/>
                                </Form.Item>
                            </Col>

                            <Col xs={24} md={8}>
                                <Form.Item label="Available Funds" name={["financial_readiness", "available_funds"]}>
                                    <InputNumber formatter={(value) => formatNumber(value)} parser={parseNumber} min={0} style={{width: "100%"}}/>
                                </Form.Item>
                            </Col>
                        </>
                    ) : (
                        <>
                            <Col xs={24} md={8}>
                                <Form.Item label="Amount Available" name={["financial_readiness", "amount_available"]} rules={[{required: true, message: "Amount available is required"}]}>
                                    <InputNumber formatter={(value) => formatNumber(value)} parser={parseNumber} min={0} style={{width: "100%"}}/>
                                </Form.Item>
                            </Col>

                            <Col xs={24} md={16}>
                                <Form.Item label="Mode of Payment" name={["financial_readiness", "payment_mode"]}>
                                    <Select options={paymentModeOptions}/>
                                </Form.Item>
                            </Col>
                        </>
                    )}
                </Row>

                <Divider/>

                <Title level={5}>Financing Terms</Title>

                <Row gutter={[16, 16]}>
                    <Col xs={24} md={8}>
                        <Form.Item label="Interest Rate" name={["financing_terms", "interest_rate"]}>
                            <InputNumber formatter={(value) => formatNumber(value)} parser={parseNumber} min={0} addonAfter="%" style={{width: "100%"}}/>
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={8}>
                        <Form.Item label="Loan Term" name={["financing_terms", "loan_term"]}>
                            <Input placeholder="Example: 5 years"/>
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={8}>
                        <Form.Item label="Monthly Payment" name={["financing_terms", "monthly_payment"]}>
                            <InputNumber formatter={(value) => formatNumber(value)} parser={parseNumber} min={0} style={{width: "100%"}}/>
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={8}>
                        <Form.Item label="Rate Type" name={["financing_terms", "rate_type"]}>
                            <Select options={rateTypeOptions}/>
                        </Form.Item>
                    </Col>
                </Row>
            </div>
        </>
    );

    /* ================= Step 3 — Cash flow ================= */

    const CashFlow = () => (
        <>
            <SectionHeader icon={<DollarOutlined/>} title="Cash Flow Analysis" desc="Review membership, tithe, remittance, reserves, and financial health."/>

            <div className="aa-section">
                <div className="aa-cashflow-wrap">
                    <div className="aa-cashflow-head">
                        <div>
                            <Text className="aa-kicker">Live Financial Profile</Text>
                            <Title level={4} className="aa-cashflow-title">Current Cash Flow Snapshot</Title>
                            <Text type="secondary">These values are automatically retrieved from the organization's latest cash-flow records.</Text>
                        </div>
                        <div className="aa-source-chip"><span className="dot"/> Synced from finance</div>
                    </div>

                    <div className="aa-manual-reserves">
                        <div className="aa-manual-reserves-head">
                            <div>
                                <Text className="aa-kicker">Manual Financial Input</Text>
                                <Title level={5} style={{margin: "2px 0"}}>Reserve Funds</Title>
                                <Text type="secondary">These are added to available funds in the funding calculation.</Text>
                            </div>
                            <Tag color="gold">Manual entry</Tag>
                        </div>

                        <Row gutter={[16, 16]}>
                            <Col xs={24} md={12}>
                                <Form.Item label="Emergency Fund Available" name={["cash_flow_analysis", "emergency_fund_available"]} rules={[{required: true, message: "Emergency fund amount is required"}]}>
                                    <InputNumber min={0} formatter={(value) => formatNumber(value)} parser={parseNumber} placeholder="Enter emergency fund" style={{width: "100%"}}/>
                                </Form.Item>
                            </Col>

                            <Col xs={24} md={12}>
                                <Form.Item label="Building Fund Available" name={["cash_flow_analysis", "building_fund_available"]} rules={[{required: true, message: "Building fund amount is required"}]}>
                                    <InputNumber min={0} formatter={(value) => formatNumber(value)} parser={parseNumber} placeholder="Enter building fund" style={{width: "100%"}}/>
                                </Form.Item>
                            </Col>
                        </Row>
                    </div>

                    <Row gutter={[16, 16]}>
                        {cashFlowMetrics.map((item) => {
                            const value = Number(cashflowdata?.[item.name] || 0);
                            return (
                                <Col xs={24} sm={12} xl={8} key={item.name}>
                                    <Tooltip title={item.type === "money" ? `Full amount: ${fullValue(value, item.type)}` : fullValue(value, item.type)}>
                                        <div className="aa-metric">
                                            <div className="aa-metric-top">
                                                <div className="aa-metric-icon-sm">{item.icon}</div>
                                                <span className="aa-metric-status">Auto-filled</span>
                                            </div>
                                            <Text className="aa-metric-label">{item.label}</Text>
                                            <div className="aa-metric-value">
                                                {item.type === "money" && <span className="aa-metric-currency">$</span>}
                                                <span>{compactValue(value, item.type)}</span>
                                            </div>
                                            <Text className="aa-metric-description">{item.description}</Text>
                                        </div>
                                    </Tooltip>
                                </Col>
                            );
                        })}
                    </Row>
                </div>
            </div>
        </>
    );

    /* ================= Step 4 — Risk & cost ================= */

    const RiskCost = () => (
        <>
            <SectionHeader icon={<SafetyCertificateOutlined/>} title="Future Cost, Risk & Income Potential" desc="Estimate future expenses, risks, and ministry growth impact."/>

            <div className="aa-section">
                <Row gutter={[16, 16]}>
                    <Col xs={24} md={8}><MetricCard title="Risk Score" value={`${metrics.riskScore}%`} icon={<SafetyCertificateOutlined/>} status={metrics.riskScore >= 70 ? "success" : "danger"} format="text"/></Col>
                    <Col xs={24} md={8}><MetricCard title="Readiness Score" value={`${metrics.readinessScore}%`} icon={<CheckCircleOutlined/>} status="success" format="text"/></Col>
                    <Col xs={24} md={8}><MetricCard title="Approval Level" value={metrics.approvalLevel} icon={<FileDoneOutlined/>} format="text"/></Col>
                </Row>

                <Divider/>
                <Title level={5}>Future Maintenance & Capital Expenditure</Title>

                <Row gutter={[16, 16]}>
                    {futureCostItems.map(([name, label]) => (
                        <Col xs={24} md={8} key={name}>
                            <Form.Item label={label} name={["future_costs", name]}>
                                <InputNumber formatter={(value) => formatNumber(value)} parser={parseNumber} min={0} style={{width: "100%"}}/>
                            </Form.Item>
                        </Col>
                    ))}
                </Row>

                <Divider/>
                <Title level={5}>Income and Revenue Potential</Title>

                <Row gutter={[16, 16]}>
                    {incomeQuestions.map(([name, label]) => (
                        <Col xs={24} md={12} key={name}>
                            <YesNoCard label={label} name={["income_potential", name]} explanationName={["income_potential", `${name}_explanation`]} explanationWhen="Yes"/>
                        </Col>
                    ))}
                </Row>

                <Row gutter={[16, 16]}>
                    <Col xs={24} md={8}>
                        <Form.Item label="How many classrooms?" name={["income_potential", "classrooms"]} rules={[{required: true, message: "Number of classrooms is required"}]}>
                            <InputNumber formatter={(value) => formatNumber(value)} parser={parseNumber} min={0} style={{width: "100%"}}/>
                        </Form.Item>
                    </Col>
                </Row>

                <Divider/>
                <Title level={5}>Financial Ratios & Health Indicators</Title>
                <Text type="secondary">Calculated automatically from the financial information provided above.</Text>

                <Row gutter={[16, 16]} style={{marginTop: 16}}>
                    <Col xs={24} md={8}><MetricCard title="Liquidity Ratio" value={metrics.liquidityRatio.toFixed(2)} icon={<WalletOutlined/>} status={metrics.liquidityRatio >= 2 ? "success" : "danger"} format="text"/></Col>
                    <Col xs={24} md={8}><MetricCard title="Debt-to-Income Ratio" value={`${metrics.debtIncomeRatio.toFixed(1)}%`} icon={<DollarOutlined/>} status={metrics.debtIncomeRatio <= 35 ? "success" : "danger"} format="text"/></Col>
                    <Col xs={24} md={8}><MetricCard title="Operating Reserve" value={`${metrics.operatingReserveMonths.toFixed(1)} Months`} icon={<SafetyCertificateOutlined/>} status={metrics.operatingReserveMonths >= 3 ? "success" : "danger"} format="text"/></Col>
                    <Col xs={24} md={8}><MetricCard title="Net Asset Position" value={metrics.netAssetPosition} icon={<BankOutlined/>} status={metrics.netAssetPosition >= 0 ? "success" : "danger"}/></Col>
                    <Col xs={24} md={8}><MetricCard title="Historical Giving Trend" value={metrics.givingTrend} icon={<LineChartOutlined/>} status={metrics.givingTrend === "Growing" ? "success" : metrics.givingTrend === "Stable" ? "" : "danger"} format="text"/></Col>
                    <Col xs={24} md={8}><MetricCard title="Checklist Completion" value={`${metrics.checklistCompletion}%`} icon={<FileDoneOutlined/>} status={metrics.checklistCompletion === 100 ? "success" : ""} format="text"/></Col>
                </Row>

                <Divider/>
                <Title level={5}>Property Acquisition Financial Readiness Checklist</Title>

                <Row gutter={[16, 16]}>
                    {checklistItems.map(([name, label]) => (
                        <Col xs={24} key={name}>
                            <YesNoCard label={label} name={["financial_checklist", name]} explanationName={["financial_checklist", `${name}_explanation`]} explanationWhen="No"/>
                        </Col>
                    ))}
                </Row>

                <Divider/>
                <Title level={5}>Risk Assessment</Title>
                <Text type="secondary">Consider financial, membership, giving, repair, and interest rate risks.</Text>

                <Row gutter={[16, 16]} style={{marginTop: 16}}>
                    {riskItems.map(([name, label]) => (
                        <Col xs={24} md={12} key={name}>
                            <YesNoCard label={label} name={["risk_assessment", name]} explanationName={["risk_assessment", `${name}_explanation`]} explanationWhen="Yes"/>
                        </Col>
                    ))}
                </Row>
            </div>
        </>
    );

    /* ================= Step 5 — Documents ================= */

    const Documents = () => {
        const docs = [
            {key: "LETTER", label: "Letter of Request", button: "Upload Letter", image: false},
            {key: "IMAGE", label: "Image of Property or Asset", button: "Upload Image", image: true},
            {key: "OTHER", label: "Other Documents", button: "Upload Other", image: false},
        ];

        return (
            <>
                <SectionHeader icon={<CloudUploadOutlined/>} title="Documents & Checklist" desc="Upload supporting documents and complete readiness checklist."/>
                <div className="aa-section">
                    <Row gutter={[16, 16]}>
                        {docs.map((doc) => (
                            <Col xs={24} md={8} key={doc.key}>
                                <Card bordered={false} className="aa-document-card">
                                    <Space direction="vertical" size={12} style={{width: "100%"}}>
                                        <div className="aa-document-icon"><CloudUploadOutlined/></div>
                                        <Title level={5}>{doc.label}</Title>
                                        <Text type="secondary">Attach supporting file.</Text>
                                        <Form.Item
                                            label={null}
                                            name={["documents", doc.key]}
                                            valuePropName="fileList"
                                            getValueFromEvent={(e) => Array.isArray(e) ? e : e?.fileList}
                                        >
                                            <Upload
                                                beforeUpload={() => false}
                                                multiple
                                                disabled={!canEdit}
                                                listType={doc.image ? "picture" : "text"}
                                                onRemove={removeDocument}
                                            >
                                                {canEdit && <Button icon={<CloudUploadOutlined/>}>{doc.button}</Button>}
                                            </Upload>
                                        </Form.Item>
                                    </Space>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                </div>
            </>
        );
    };

    /* ================= Step 6 — Review ================= */

    const Review = () => (
        <>
            <SectionHeader icon={<FileSearchOutlined/>} title="Executive Review" desc="Final approval-ready summary before submission."/>

            <Divider/>
            <Title level={5}>Finance Department Review</Title>

            <Alert
                type="info"
                showIcon
                message="The Finance Department verifies whether sufficient funds are available within the approved budget and evaluates the organization's financial capacity to acquire the asset."
                style={{marginBottom: 20}}
            />

            <Row gutter={[16, 16]}>
                <Col xs={24} md={8}><MetricCard title="Budget Allocation" value={metrics.budgetAllocationStatus} icon={<WalletOutlined/>} status={metrics.budgetAllocationStatus === "Available" ? "success" : "danger"} format="text"/></Col>
                <Col xs={24} md={8}><MetricCard title="Funding Source" value={metrics.fundingSourceStatus} icon={<BankOutlined/>} status={metrics.fundingSourceStatus === "Confirmed" ? "success" : "danger"} format="text"/></Col>
                <Col xs={24} md={8}><MetricCard title="Affordability" value={metrics.affordabilityStatus} icon={<DollarOutlined/>} status={metrics.affordabilityStatus === "Affordable" ? "success" : "danger"} format="text"/></Col>
            </Row>

            <Card style={{marginTop: 20}}>
                <Descriptions bordered size="small" column={1}>
                    <Descriptions.Item label="Budget Allocation">{metrics.budgetAllocationRemarks}</Descriptions.Item>
                    <Descriptions.Item label="Funding Source">{metrics.fundingSourceRemarks}</Descriptions.Item>
                    <Descriptions.Item label="Affordability Assessment">{metrics.affordabilityRemarks}</Descriptions.Item>
                    <Descriptions.Item label="Finance Recommendation">
                        <Tag color={metrics.financeRecommendation === "APPROVED" ? "green" : "red"}>{metrics.financeRecommendation}</Tag>
                    </Descriptions.Item>
                </Descriptions>
            </Card>

            <div className="aa-section">
                <Row gutter={[16, 16]}>
                    <Col xs={24} lg={8}>
                        <Card bordered={false} className="aa-review-score-card">
                            <Progress type="circle" percent={metrics.readinessScore}/>
                            <Title level={4}>Readiness Score</Title>
                            <Text type="secondary">45% funding coverage, 30% checklist health, 25% risk score.</Text>
                        </Card>
                    </Col>

                    <Col xs={24} lg={16}>
                        <Card bordered={false} className="aa-executive-card">
                            <div className="aa-executive-title">
                                <Title level={4}>Asset Acquisition Summary</Title>
                                <Tag color="blue">{metrics.approvalLevel} Approval</Tag>
                            </div>

                            <Divider/>

                            <Row gutter={[16, 16]}>
                                {[
                                    ["Asset Type", reviewValues.asset_type],
                                    ["Priority", reviewValues.priority_level],
                                    ["Method", reviewValues.acquisition_method],
                                    ["Estimated Cost", money(metrics.estimatedCost)],
                                    ["Available Funds", money(metrics.availableFunds)],
                                    ["Funding Gap", money(metrics.fundingGap)],
                                    ["Funding Readiness", `${metrics.fundingPercent}%`],
                                    ["Risk Score", `${metrics.riskScore}%`],
                                ].map(([label, value]) => (
                                    <Col xs={24} md={12} key={label}>
                                        <div className="aa-review-line">
                                            <Text type="secondary">{label}</Text>
                                            <Text strong>{value || "-"}</Text>
                                        </div>
                                    </Col>
                                ))}
                            </Row>

                            <Divider/>

                            <Text type="secondary">Justification</Text>
                            <p className="aa-review-text">{reviewValues.justification || "-"}</p>
                        </Card>
                    </Col>

                    <Col xs={24}>
                        <Card bordered={false} className="aa-executive-card">
                            <Title level={5}>Approval Workflow</Title>
                            <Timeline
                                items={[
                                    {color: "green", children: "Local submission"},
                                    {color: metrics.approvalLevel !== "Local" ? "blue" : "gray", children: "District review"},
                                    {color: ["Area", "National"].includes(metrics.approvalLevel) ? "blue" : "gray", children: "Area review"},
                                    {color: metrics.approvalLevel === "National" ? "blue" : "gray", children: "National approval"},
                                ]}
                            />
                        </Card>
                    </Col>
                </Row>
            </div>
        </>
    );

    /* ================= layout ================= */

    const stepPanels = [BasicRequest, AssetDetailsWrapper, Funding, CashFlow, RiskCost, Documents, Review];

    if (pageLoading) {
        return <div style={{padding: 40, textAlign: "center"}}><Text>Loading asset request...</Text></div>;
    }

    return (
        <div className="aa-page">
            <div className="aa-shell">
                <header className="aa-masthead">
                    <div className="aa-masthead-left">
                        <div className="aa-seal"><ApartmentOutlined/></div>
                        <div>
                            <Text className="aa-kicker">Asset &middot; {requestId ? "Acquisition Request" : "New Acquisition"}</Text>
                            <Title level={2} className="aa-masthead-title">Asset Acquisition Management</Title>
                            <Text type="secondary">Plan, create, and track the asset acquisition life cycle.</Text>
                        </div>
                    </div>

                    <div className="aa-masthead-right">
                        <div className="aa-status-pill"><span className="dot"/> <Text strong>{requestId ? `${requestData?.current_level || "LOCAL"} · ${requestData?.status || "DRAFT"}` : "NEW REQUEST"}</Text></div>
                        {requestId && <Tag>Request #{requestId}</Tag>}
                        {/*{canEdit && <Button loading={loading} onClick={saveDraft}>{requestId ? "Save Changes" : "Save Draft"}</Button>}*/}
                    </div>
                </header>

                <section className="aa-ledger-wrap">
                    <div className="aa-ledger-head">
                        <div>
                            <Text className="aa-kicker">Workflow</Text>
                            <Text strong>&nbsp;{STEP_TITLES[current]}</Text>
                        </div>
                        <Text type="secondary">Stage {current + 1} of {STEP_TITLES.length} &middot; {completion}% complete</Text>
                    </div>

                    <Steps current={current} responsive size="small" items={stepItems} className="aa-ledger-steps" onChange={handleLedgerChange}/>
                    <Progress percent={completion} showInfo={false} strokeLinecap="square"/>
                </section>

                <main className="aa-board">
                    <section className="aa-main-col">
                        <Card className="aa-sheet" bordered={false}>
                            <div className="aa-sheet-head">
                                <div>
                                    <Text className="aa-kicker">Current section</Text>
                                    <Title level={3} className="aa-sheet-title">{STEP_TITLES[current]}</Title>
                                </div>
                                <Tag className="aa-step-index" style={{width: "120px"}}>Stage {current + 1} of {STEP_TITLES.length}</Tag>
                            </div>

                            <Form form={form} {...CisUI().formItemLayout} className="ant-advanced-search-form aa-form" initialValues={initialFormValues} onValuesChange={handleValuesChange} onFinish={onFinish} onFinishFailed={onFinishFailed} disabled={!canEdit}>
                                {stepPanels.map((Panel, i) => (
                                    <div key={STEP_TITLES[i]} className="aa-step-content" style={{display: current === i ? "block" : "none"}}>
                                        {Panel()}
                                    </div>
                                ))}
                            </Form>

                            <footer className="aa-actionbar">
                                <div>
                                    {current > 0 && <Button icon={<ArrowLeftOutlined/>} onClick={prev}>Previous stage</Button>}
                                </div>
                                <Space>
                                    {canEdit && <Button loading={loading} onClick={saveDraft}>{requestId ? "Save Changes" : "Save Draft"}</Button>}
                                    {current < STEP_TITLES.length - 1 ? (
                                        <Button type="primary" onClick={next} disabled={!canEdit}>Continue <ArrowRightOutlined/></Button>
                                    ) : (
                                        canEdit && <Button type="primary" icon={<SendOutlined/>} loading={loading} onClick={submitRequest}>{requestData?.status === "RETURNED" ? "Resubmit Request" : "Submit Request"}</Button>
                                    )}
                                </Space>
                            </footer>
                        </Card>
                    </section>

                    <aside className="aa-side">
                        <div className="aa-side-sticky">
                            <Card className="aa-side-card" bordered={false}>
                                <div className="aa-side-title-row">
                                    <div>
                                        <Text className="aa-kicker">Summary</Text>
                                        <Title level={4} className="aa-side-title">Decision snapshot</Title>
                                    </div>
                                    <Tooltip title="Recalculates instantly as you type, from every stage.">
                                        <InfoCircleOutlined className="aa-info-icon"/>
                                    </Tooltip>
                                </div>

                                <div className="aa-big-figure">
                                    <Text type="secondary">Estimated acquisition</Text>
                                    <Title level={2}><CompactAmount value={metrics.estimatedCost}/></Title>
                                </div>

                                <div className="aa-side-rows">
                                    <div className="aa-side-row">
                                        <span><WalletOutlined/> Available funds</span>
                                        <strong><CompactAmount value={metrics.availableFunds}/></strong>
                                    </div>
                                    <div className={`aa-side-row ${metrics.fundingGap > 0 ? "is-alert" : "is-good"}`}>
                                        <span><SafetyCertificateOutlined/> Funding gap</span>
                                        <strong><CompactAmount value={metrics.fundingGap}/></strong>
                                    </div>
                                    <div className="aa-side-row">
                                        <span><ClockCircleOutlined/> Current stage</span>
                                        <strong>{current + 1}/{STEP_TITLES.length}</strong>
                                    </div>
                                </div>

                                <Divider/>

                                <div className="aa-ring-wrap">
                                    <div>
                                        <div className="aa-health-row">
                                            <Text strong>Readiness index</Text>
                                            <Tag color={metrics.readinessScore >= 75 ? "green" : metrics.readinessScore >= 50 ? "gold" : "red"}>
                                                {metrics.readinessScore >= 75 ? "On track" : metrics.readinessScore >= 50 ? "Needs attention" : "At risk"}
                                            </Tag>
                                        </div>
                                        <Text type="secondary">Funding 45% &middot; checklist 30% &middot; risk 25%</Text>
                                    </div>
                                    <Progress type="circle" percent={metrics.readinessScore} size={82}/>
                                </div>

                                <div className="aa-funding-meter">
                                    <div>
                                        <Text strong>Funding coverage</Text>
                                        <br/>
                                        <Text type="secondary">{metrics.fundingPercent}% of estimated cost</Text>
                                    </div>
                                    <Progress percent={metrics.fundingPercent} showInfo={false}/>
                                </div>
                            </Card>

                            <Card className="aa-route-card" bordered={false}>
                                <Text className="aa-kicker">Approval Process</Text>
                                <br/>
                                <Text type="secondary">Routing sequence for this acquisition request</Text>

                                <div className="aa-route-list">
                                    {[
                                        ["Local review", "Request preparation and initial verification"],
                                        ["District review", "Operational and financial assessment"],
                                        ["Area review", "Area governance recommendation"],
                                        ["National approval", "Final authorization when required"],
                                    ].map(([label, description], index) => (
                                        <div className={`aa-route-step ${index === 0 ? "active" : ""}`} key={label}>
                                            <div className="aa-route-num">{String(index + 1).padStart(2, "0")}</div>
                                            <div className="aa-route-copy">
                                                <Text strong>{label}</Text>
                                                <br/>
                                                <Text type="secondary">{description}</Text>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </Card>
                        </div>
                    </aside>
                </main>
            </div>
        </div>
    );
}
